/**
 * CTO Directive #120.2 — Founder Greeting.
 * CTO Directive #126.2 — Clickable "audio acceptance" modal REMOVED.
 *
 * The one-shot founder greeting hook is preserved (it still plays
 * through the real audio chain), but the modal-driven
 * "Duyuyorum / Duymuyorum" prompt has been permanently retired. ME
 * now understands audio-state intents from the natural conversation
 * via ``POST /api/me/voice/classify-utterance`` — no button clicks,
 * no focus-trap overlay, no dashboard-style form.
 */

import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { playSpokenAudio, primeAudioOnFirstGesture } from "@/lib/audioChain";

// A stable, per-tab session id — kept in sessionStorage so a reload
// does not re-trigger the greeting but a fresh tab does.
function getBrowserSessionId() {
  if (typeof window === "undefined") return "srv-render";
  const key = "meai:browser_session_id";
  let sid = window.sessionStorage.getItem(key);
  if (!sid) {
    sid = "bs-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
    window.sessionStorage.setItem(key, sid);
  }
  return sid;
}

export function useFounderGreeting({ patientContextActive = false } = {}) {
  const firedRef = useRef(false);
  const [state, setState] = useState({ greeted: false, text: "" });

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    primeAudioOnFirstGesture();
    (async () => {
      try {
        const r = await api.post("/me/voice/founder-greeting", {
          browser_session_id: getBrowserSessionId(),
          patient_context_active: Boolean(patientContextActive),
        });
        const body = r.data || {};
        if (!body.greeted) {
          setState({ greeted: false, text: "" });
          return;
        }
        setState({ greeted: true, text: body.text_tr });
        if (body.audio_base64) {
          playSpokenAudio({
            audioBase64: body.audio_base64,
            mimeType: body.mime_type || "audio/mpeg",
            utteranceId: "founder-greeting-" + getBrowserSessionId(),
          });
        }
      } catch {
        // Silent — a network hiccup must not surface the founder
        // identity in an error banner.
      }
    })();
  }, [patientContextActive]);

  return state;
}
